import LocationPinSvg from 'components/_common/Icons/SVGIcons/LocationPinSvg';
import VerifiedBadgeSvg from 'components/_common/Icons/SVGIcons/VerifiedBadgeSvg';
import { getShippingFFLObj } from 'helpers/DataHelper';
import { getGuestDeliveryLocation, storeUserInfo } from 'helpers/StorageHelper';
import { t } from 'i18next';
import { useMemo, useCallback, useEffect } from 'react';
import { updateUserState } from 'store/auth/actions/AuthAction';
import { userInfoSelector, userProfileInfoSelector } from 'store/auth/selectors/authSelectors';
import { getAddressList } from 'store/customer/actions/CustomerAddressAction';
import { selectedDeliveryAddressSelector } from 'store/customer/selectors/CustomerGlobalSelectors';
import { setCheckoutSelectedShippingFfl } from 'store/customer/slices/CustomerCheckoutSlice';
import {
  setDeliveryLocationModalOpen,
  setGuestDeliveryFFLModalOpen,
  setSelectedDeliveryAddress,
} from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import 'modules/customer/assets/scss/customerGeneralProfile.scss';

// You can pass dynamic props here if needed in the future
export default function DeliveryLocationBlock() {
  const dispatch = useAppDispatch();
  const profileInfo = useAppSelector(userProfileInfoSelector);
  const userInfo = useAppSelector(userInfoSelector);
  // kept in the store so the profile dropdown / mobile drawer show the same FFL
  const activeAddress = useAppSelector(selectedDeliveryAddressSelector);

  const setActiveAddress = useCallback(
    (address: any) => {
      dispatch(setSelectedDeliveryAddress(address));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!profileInfo?.id) {
      // guests have no address book to read back — restore the FFL they picked earlier
      const storedLocation = getGuestDeliveryLocation();
      setActiveAddress(storedLocation);
      dispatch(setCheckoutSelectedShippingFfl(storedLocation?.selected_ffl ?? null));
    } else {
      getAddressList({ page: 1, limit: 20 })
        .then((res: any) => {
          if (res?.status) {
            const list = Array.isArray(res?.data?.data) ? res.data.data : [];
            const selectedId = profileInfo?.selected_address_id;
            const chosenAdd =
              (selectedId && list.find((a: any) => (a.id || a._id) === selectedId)) ||
              list.find((a: any) => a.is_default) ||
              list[0] ||
              null;
            setActiveAddress(chosenAdd);
            if (chosenAdd) {
              const addrId = chosenAdd.id || chosenAdd._id;
              if (profileInfo && profileInfo.selected_address_id !== addrId) {
                const userData = {
                  ...userInfo,
                  user: {
                    ...profileInfo,
                    selected_address_id: addrId,
                  },
                };
                storeUserInfo(userData);
                updateUserState(userData);
              }
            }
          }
        })
        .catch((err) => console.error(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileInfo?.id]);

  const displayInfo = useMemo(() => {
    if (!activeAddress) {
      return {
        zip: t('select_zip', { defaultValue: 'Select Zip' }),
        dealer: t('set_delivery_location', { defaultValue: 'Set Delivery Location' }),
        isRegisteredFfl: false,
      };
    }

    const zipCode =
      activeAddress?.zipcode ||
      activeAddress?.zip_code ||
      (typeof activeAddress?.pincode === 'object'
        ? activeAddress?.pincode?.name
        : activeAddress?.pincode) ||
      '';

    const city =
      (typeof activeAddress?.city === 'object'
        ? activeAddress?.city?.name
        : activeAddress?.city_name) ||
      activeAddress?.city ||
      '';
    const state =
      (typeof activeAddress?.state === 'object'
        ? activeAddress?.state?.name
        : activeAddress?.state_name) ||
      activeAddress?.state ||
      '';

    let dealerName = [city, state].filter(Boolean).join(', ');
    if (!dealerName && activeAddress?.first_name) {
      dealerName = 'Home';
    }

    const fflObj = activeAddress?.selected_ffl
      ? getShippingFFLObj(activeAddress.selected_ffl)
      : null;
    if (fflObj && (fflObj.label || fflObj.company_name)) {
      dealerName = (fflObj.label || fflObj.company_name).toUpperCase();
    }

    // Same "isApproved" distinction the FFL listing cards use for their featured badge -
    // a claimed/registered FFL has its business profile nested under .ffl, while a bare
    // unclaimed ATF license record (or a plain home address with no FFL at all) doesn't.
    const isRegisteredFfl = !!fflObj?.ffl;

    return {
      zip: zipCode || t('select_zip', { defaultValue: 'Select Zip' }),
      dealer: dealerName || t('set_delivery_location', { defaultValue: 'Set Delivery Location' }),
      isRegisteredFfl,
    };
  }, [activeAddress]);

  const handleOpenModal = useCallback(() => {
    // guests have no address book — they pick an FFL on the map instead
    if (!profileInfo?.id) {
      dispatch(setGuestDeliveryFFLModalOpen(true));
      return;
    }
    dispatch(setDeliveryLocationModalOpen(true));
  }, [profileInfo, dispatch]);

  return (
    <div className="d-flex align-items-center cursor-pointer deliver-row" onClick={handleOpenModal}>
      <LocationPinSvg className="delivery-pin-icon" />
      <div className="delivery-text">
        <span className="deliver-label">
          {t('deliver_to')} - <span className="deliver-zip">{displayInfo.zip}</span>
        </span>
        <span className="deliver-dealer">
          <span className="deliver-dealer-name">{displayInfo.dealer}</span>{' '}
          {displayInfo.isRegisteredFfl && (
            <VerifiedBadgeSvg
              width={14}
              height={14}
              fill="currentColor"
              className="verified-check-icon"
            />
          )}
        </span>
      </div>
    </div>
  );
}
